import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Send, MessageCircle } from 'lucide-react';
import { trackEvent } from '../../lib/track';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const JO_PHOTO =
  'https://customer-assets.emergentagent.com/job_2b245c66-15c7-42b0-87c8-31e0db61a275/artifacts/7q6v7uyu_Screenshot%202026-03-10%20at%2012.57.33.png';

// Quick-reply chips shown until the visitor sends their first message. They
// nudge the visitor to remember Audio & Co. does more than the Pro Plan while
// staying single-tap easy on mobile.
const QUICK_REPLIES = [
  { label: 'Tell me about the Pro Plan', prompt: 'Tell me about the Pro Plan.' },
  { label: 'Book publishing', prompt: 'Can Jo help me publish a book?' },
  { label: 'Audiobook recording', prompt: 'Do you record and produce audiobooks?' },
  { label: 'Podcast management only', prompt: 'I already have a podcast. Can you manage it (not the full Pro Plan)?' },
  { label: 'Website or app build', prompt: 'I need a new website or app. Can you build one?' },
];

/**
 * BiliChat, Audio & Co.'s always-on customer-services bot.
 * Floating button bottom-right. Uses Jo's photo as the bot avatar.
 * Session persists in sessionStorage so a page reload keeps history.
 */
export default function BiliChat() {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null); // {session_id, messages}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Bootstrap session on first open, restore from sessionStorage otherwise.
  useEffect(() => {
    if (!open || session) return;
    (async () => {
      try {
        const stored = sessionStorage.getItem('bili_session_v2');
        if (stored) {
          setSession(JSON.parse(stored));
          return;
        }
        const res = await axios.post(`${API}/bili/start`, { page_url: window.location.pathname });
        const s = {
          session_id: res.data.session_id,
          messages: [{ role: 'assistant', content: res.data.greeting }],
        };
        setSession(s);
        sessionStorage.setItem('bili_session_v2', JSON.stringify(s));
      } catch (e) {
        setSession({
          session_id: null,
          messages: [{ role: 'assistant',
            content: "Hi! I'm having connection issues right now. In the meantime, book a call at audioandco.com/book." }],
        });
      }
    })();
  }, [open, session]);

  // Persist session to sessionStorage on every change.
  useEffect(() => {
    if (session?.session_id) {
      try { sessionStorage.setItem('bili_session_v2', JSON.stringify(session)); } catch (e) { /* noop */ }
    }
  }, [session]);

  // Scroll to bottom on new message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [session, sending]);

  const send = async (e, overrideText) => {
    e?.preventDefault?.();
    const text = (overrideText ?? input).trim();
    if (!text || sending || !session?.session_id) return;
    setSending(true);
    const userMsg = { role: 'user', content: text };
    setSession((s) => ({ ...s, messages: [...s.messages, userMsg] }));
    setInput('');
    try {
      const res = await axios.post(`${API}/bili/message`, {
        session_id: session.session_id,
        message: text,
      });
      setSession((s) => ({
        ...s,
        messages: [...s.messages, { role: 'assistant', content: res.data.reply }],
      }));
    } catch (e) {
      setSession((s) => ({
        ...s,
        messages: [...s.messages, {
          role: 'assistant',
          content: "Sorry, I hit a snag. Try again in a sec, or book a call directly at audioandco.com/book.",
        }],
      }));
    } finally {
      setSending(false);
    }
  };

  // True until the visitor has sent at least one message. Drives quick-reply chips.
  const hasUserSent = (session?.messages || []).some((m) => m.role === 'user');

  // Render blog-slug links as clickable
  const renderMessage = (content) => {
    // detect URLs (audioandco.com/xxx or /blog/xxx)
    const parts = content.split(/(audioandco\.com\/[\w\-/]+|\/blog\/[\w\-]+|\/pro-plan|\/book|\/authority-hub|\/results|\/about)/g);
    return parts.map((part, i) => {
      if (part.match(/^audioandco\.com\//)) {
        const path = part.replace(/^audioandco\.com/, '');
        return <a key={i} href={path} className="text-[#BC516F] font-semibold underline">{part}</a>;
      }
      if (part.match(/^\/(blog|pro-plan|book|authority-hub|results|about)/)) {
        return <a key={i} href={part} className="text-[#BC516F] font-semibold underline">{part}</a>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <>
      {/* Floating open button */}
      {!open && (
        <button
          onClick={() => { setOpen(true); trackEvent('bili_open'); }}
          className="fixed bottom-24 sm:bottom-5 right-4 sm:right-5 z-[60] flex items-center gap-2.5 bg-white shadow-[0_10px_30px_rgba(30,42,68,0.15)] border border-[#E2E8F0] hover:shadow-[0_14px_40px_rgba(30,42,68,0.22)] rounded-full pl-2 pr-4 py-2 transition-all hover:-translate-y-0.5"
          data-testid="bili-open"
          aria-label="Chat with Bili"
        >
          <img src={JO_PHOTO} alt="Bili, the Audio & Co. AI assistant for podcast, publishing and SEO website questions" title="Chat to Bili | Audio & Co." className="w-9 h-9 rounded-full object-cover object-top border-2 border-[#BC516F]" />
          <span className="hidden sm:inline text-sm font-semibold text-[#1E2A44]">
            Chat to <span className="text-[#BC516F]">Bili</span>
          </span>
          <MessageCircle className="w-4 h-4 text-[#BC516F] sm:hidden" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#22C55E] border-2 border-white" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-2 right-2 sm:bottom-4 sm:right-4 z-[70] w-[calc(100vw-16px)] sm:w-[380px] h-[calc(100dvh-80px)] sm:h-[560px] sm:max-h-[80vh] bg-white rounded-[16px] shadow-[0_20px_60px_rgba(30,42,68,0.25)] border border-[#E2E8F0] flex flex-col overflow-hidden"
          data-testid="bili-panel"
        >
          {/* Header */}
          <div className="bg-[#1E2A44] px-4 py-3 flex items-center gap-3">
            <img src={JO_PHOTO} alt="Bili, the Audio & Co. AI assistant for podcast, publishing and SEO website questions" title="Chat to Bili | Audio & Co." className="w-9 h-9 rounded-full object-cover object-top border-2 border-[#BC516F]" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm leading-tight">Bili</p>
              <p className="text-white/60 text-xs">Jo&apos;s assistant · usually replies instantly</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white transition"
              aria-label="Close chat"
              data-testid="bili-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F6F8FB]"
            data-testid="bili-messages"
          >
            {(session?.messages || []).map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`bili-message-${i}`}
              >
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-[14px] text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-[#BC516F] text-white rounded-br-[4px]'
                      : 'bg-white text-[#1E2A44] border border-[#E2E8F0] rounded-bl-[4px] shadow-[0_2px_8px_rgba(30,42,68,0.04)]'
                  }`}
                >
                  {m.role === 'user' ? m.content : renderMessage(m.content)}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="bili-typing">
                <div className="bg-white border border-[#E2E8F0] rounded-[14px] rounded-bl-[4px] px-4 py-3 flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-[#94A3B8] animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-[#94A3B8] animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-[#94A3B8] animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}

            {/* Quick-reply chips, shown until the visitor sends their first message.
                Keeps the fuller menu of services one tap away without cluttering
                the greeting. */}
            {!hasUserSent && !sending && (session?.messages?.length || 0) > 0 && (
              <div className="pt-1" data-testid="bili-quick-replies">
                <p className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-2">Quick asks</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_REPLIES.map((q) => (
                    <button
                      key={q.label}
                      type="button"
                      onClick={() => { trackEvent('bili_chip_click', q.label); send(null, q.prompt); }}
                      className="text-xs font-medium px-3 py-2 bg-white border border-[#E2E8F0] rounded-full text-[#1E2A44] hover:border-[#BC516F] hover:text-[#BC516F] transition shadow-[0_2px_6px_rgba(30,42,68,0.04)]"
                      data-testid={`bili-quick-${q.label.toLowerCase().replace(/\s+/g,'-')}`}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="border-t border-[#E2E8F0] bg-white p-3 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything…"
              disabled={sending}
              className="flex-1 px-3 py-2.5 bg-[#F6F8FB] border border-[#E2E8F0] rounded-full text-sm text-[#1E2A44] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#BC516F] transition disabled:opacity-60"
              data-testid="bili-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-[#BC516F] hover:bg-[#9A3A54] text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Send"
              data-testid="bili-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
